/**
 * I18n Translation System
 *
 * - English (en.json) is the source of truth for structure and keys
 * - All other language files must have matching structure
 * - Add a new language: create XX.json file and add to LANGUAGE_FILES
 */

import en from './en.json';
import es from './es.json';
import de from './de.json';
import it from './it.json';
import fr from './fr.json';

// Translation type derived from the source of truth (English)
export type Translation = typeof en;

// Available languages
export type Language = 'en' | 'es' | 'de' | 'it' | 'fr';

export const AVAILABLE_LANGUAGES: Language[] = ['en', 'es', 'de', 'it', 'fr'];

// Language display names (can be translated themselves)
export const LANGUAGE_NAMES: Record<Language, string> = {
	en: 'English',
	es: 'Español',
	de: 'Deutsch',
	it: 'Italiano',
	fr: 'Français',
};

// All translation files - ADD NEW LANGUAGES HERE
const LANGUAGE_FILES: Record<Language, Translation> = {
	en,
	es,
	de,
	it,
	fr,
};

/**
 * Get translations for a specific language
 * @param lang - Language code (e.g., 'es', 'de')
 * @param fallbackLang - Fallback language (default: 'en')
 * @returns Translation object
 */
export function getTranslations(
	lang: string = 'en',
	fallbackLang: Language = 'en'
): Translation {
	// Normalize language code (handle variants like 'es_ES' -> 'es')
	const normalizedLang = lang.split(/[_-]/)[0] as Language;

	if (normalizedLang in LANGUAGE_FILES) {
		return LANGUAGE_FILES[normalizedLang];
	}

	// Fallback to default language
	return LANGUAGE_FILES[fallbackLang];
}

/**
 * Get translations for a language with overrides applied.
 * Missing keys fall back to English; overrides are flat dot-path maps
 * (e.g. { 'tradePartner.title': 'Distribuidor' }) from site/event settings.
 * Unknown keys are ignored.
 */
export function getTranslationsWithOverrides(
	lang: string = 'en',
	overrides: Record< string, string > = {}
): Translation {
	const merged = deepMerge( en, getTranslations( lang ) ) as Translation;

	for ( const [ path, text ] of Object.entries( overrides ) ) {
		if ( typeof text !== 'string' || text === '' ) continue;
		const keys = path.split( '.' );
		const last = keys.pop() as string;
		let node: any = merged;
		for ( const k of keys ) {
			node = node?.[ k ];
		}
		if ( node && typeof node[ last ] === 'string' ) {
			node[ last ] = text;
		}
	}

	return merged;
}

function deepMerge( base: any, extra: any ): any {
	const out: any = Array.isArray( base ) ? [ ...base ] : { ...base };
	for ( const [ key, value ] of Object.entries( extra ?? {} ) ) {
		out[ key ] =
			value && typeof value === 'object' && ! Array.isArray( value )
				? deepMerge( out[ key ] ?? {}, value )
				: value;
	}
	return out;
}

/**
 * Get a nested value from translation object using dot notation
 * @param translations - Translation object
 * @param key - Dot-notation key (e.g., 'header.title')
 * @returns The translated string or the key if not found
 */
export function t(
	translations: Translation,
	key: string,
	defaultValue?: string
): string {
	const keys = key.split('.');
	let value: any = translations;

	for (const k of keys) {
		value = value?.[k];
		if (value === undefined) {
			return defaultValue ?? key;
		}
	}

	return value ?? defaultValue ?? key;
}

/**
 * Validate that all language files have the same keys as the source (English)
 * Logs missing keys to console during development
 */
export function validateTranslations(): void {
	if (process.env.NODE_ENV !== 'production') {
		const sourceKeys = getKeys(en);

		for (const [lang, translations] of Object.entries(LANGUAGE_FILES)) {
			if (lang === 'en') continue;

			const langKeys = getKeys(translations);
			const missing = difference(sourceKeys, langKeys);
			const extra = difference(langKeys, sourceKeys);

			if (missing.length > 0) {
				console.warn(
					`[i18n] ${lang} is missing keys:`,
					missing.slice(0, 5).join(', '),
					missing.length > 5 ? `...and ${missing.length - 5} more` : ''
				);
			}

			if (extra.length > 0) {
				console.warn(
					`[i18n] ${lang} has extra keys (not in en):`,
					extra.slice(0, 5).join(', '),
					extra.length > 5 ? `...and ${extra.length - 5} more` : ''
				);
			}
		}
	}
}

/**
 * Get all dot-notation keys from a nested object
 */
function getKeys(obj: any, prefix = ''): string[] {
	const keys: string[] = [];

	for (const [key, value] of Object.entries(obj)) {
		const fullKey = prefix ? `${prefix}.${key}` : key;

		if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
			keys.push(...getKeys(value, fullKey));
		} else {
			keys.push(fullKey);
		}
	}

	return keys;
}

/**
 * Get items in array1 that are not in array2
 */
function difference(array1: string[], array2: string[]): string[] {
	const set2 = new Set(array2);
	return array1.filter((item) => !set2.has(item));
}

// Auto-validate in development
if (typeof window !== 'undefined') {
	validateTranslations();
}

// Export for backward compatibility
export { getTranslations as translations };

// Re-export type for use in components
export type { Translation as UIText };
